import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import "./styles/Revenue.css";

const API_PAYMENTS = "https://salathai.co.tz/mrms/Payment.php";
const API_EXPENSES = "https://salathai.co.tz/mrms/Expenses.php";

export default function Revenue() {
  const token = localStorage.getItem("token");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const paymentMethods = ["CASH", "M-PESA", "MIX BY YAS", "SELECOM", "CRDB BANK"];

  useEffect(() => {
    fetchReport();
  }, [selectedDate]);

  const formatDate = (date) => {
    return date.toISOString().slice(0, 10);
  };

  const fetchReport = async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    
    const currentDateStr = formatDate(selectedDate);

    try {
      // Fetch payments for selected date
      const payRes = await fetch(API_PAYMENTS, { headers });
      const payments = await payRes.json();
      
      // Fetch expenses for selected date
      const expRes = await fetch(
        `${API_EXPENSES}?date=${currentDateStr}`,
        { headers }
      );
      const expData = await expRes.json();
      
      // Calculate TODAY's revenue
      let todayRevenue = {
        "CASH": 0,
        "M-PESA": 0,
        "MIX BY YAS": 0,
        "SELECOM": 0,
        "CRDB BANK": 0
      };
      
      if (Array.isArray(payments)) {
        payments.forEach(payment => {
          if (payment.status !== "PAID") return;
          const paymentDate = formatDate(new Date(payment.created_at));
          let method = payment.payment_method;
          
          // Convert payment method names to match our keys
          if (method === "CRDB_BANK") method = "CRDB BANK";
          if (method === "MIX_BY_YAS") method = "MIX BY YAS";
          if (method === "MIXX_BY_YAS") method = "MIX BY YAS";
          
          // Only include payments from today
          if (paymentDate === currentDateStr) {
            if (todayRevenue[method] !== undefined) {
              todayRevenue[method] += parseFloat(payment.total_amount || 0);
            }
          }
        });
      }
      
      // Calculate TODAY's expenses
      let todayExpenses = {
        "CASH": 0,
        "M-PESA": 0,
        "MIX BY YAS": 0,
        "SELECOM": 0,
        "CRDB BANK": 0
      };
      
      let expensesList = [];
      
      if (expData.expenses && Array.isArray(expData.expenses)) {
        expensesList = expData.expenses;
        expData.expenses.forEach(expense => {
          const expenseDate = formatDate(new Date(expense.created_at));
          let method = expense.payment_method;
          
          // Convert expense payment method names to match our keys
          if (method === "CRDB_BANK") method = "CRDB BANK";
          if (method === "MIX_BY_YAS") method = "MIX BY YAS";
          if (method === "MIXX_BY_YAS") method = "MIX BY YAS";
          
          // Only include expenses from today
          if (expenseDate === currentDateStr) {
            if (todayExpenses[method] !== undefined) {
              todayExpenses[method] += parseFloat(expense.amount || 0);
            }
          }
        });
      }
      
      // Calculate TODAY's closing balance = Revenue - Expenses
      const closingBalance = {};
      let totalRevenueSum = 0;
      let totalExpensesSum = 0;
      
      paymentMethods.forEach(method => {
        const revToday = todayRevenue[method] || 0;
        const expToday = todayExpenses[method] || 0;
        
        totalRevenueSum += revToday;
        totalExpensesSum += expToday;
        
        closingBalance[method] = revToday - expToday;
      });
      
      setReportData({
        date: currentDateStr,
        revenue: todayRevenue,
        expenses: todayExpenses,
        closing: closingBalance,
        totalRevenue: totalRevenueSum,
        totalExpenses: totalExpensesSum,
        totalClosing: Object.values(closingBalance).reduce((a, b) => a + b, 0),
        expensesList: expensesList.filter(e => formatDate(new Date(e.created_at)) === currentDateStr)
      });
      
    } catch (error) {
      console.error("Error fetching report:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (value) => {
    if (!value && value !== 0) return "0";
    return Math.round(value).toLocaleString();
  };
  
  const getMethodDisplayName = (method) => {
    const mapping = {
      "CASH": "Cash",
      "M-PESA": "M-PESA",
      "MIX BY YAS": "Mix By Yas",
      "SELECOM": "Selecom",
      "CRDB BANK": "CRDB Bank"
    };
    return mapping[method] || method;
  };
  
  const exportExcel = () => {
    if (!reportData) return;
    
    const wsData = [
      ["REVENUE REPORT"],
      [`Date: ${selectedDate.toLocaleDateString()}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      ["DAILY SUMMARY"],
      ["Description", "Cash", "M-PESA", "Mix By Yas", "Selecom", "CRDB Bank", "Total"],
      [
        "Revenue",
        formatCurrency(reportData.revenue["CASH"]),
        formatCurrency(reportData.revenue["M-PESA"]),
        formatCurrency(reportData.revenue["MIX BY YAS"]),
        formatCurrency(reportData.revenue["SELECOM"]),
        formatCurrency(reportData.revenue["CRDB BANK"]),
        formatCurrency(reportData.totalRevenue)
      ],
      [
        "Expenses",
        formatCurrency(reportData.expenses["CASH"]),
        formatCurrency(reportData.expenses["M-PESA"]),
        formatCurrency(reportData.expenses["MIX BY YAS"]),
        formatCurrency(reportData.expenses["SELECOM"]),
        formatCurrency(reportData.expenses["CRDB BANK"]),
        formatCurrency(reportData.totalExpenses)
      ],
      [
        "Closing Balance",
        formatCurrency(reportData.closing["CASH"]),
        formatCurrency(reportData.closing["M-PESA"]),
        formatCurrency(reportData.closing["MIX BY YAS"]),
        formatCurrency(reportData.closing["SELECOM"]),
        formatCurrency(reportData.closing["CRDB BANK"]),
        formatCurrency(reportData.totalClosing)
      ]
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Revenue Report");
    
    const fileBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([fileBuffer], { type: "application/octet-stream" }),
      `Revenue_Report_${formatDate(selectedDate)}.xlsx`
    );
  };
  
  return (
    <div className="revenue-page">
      <div className="revenue-header">
        <div className="header-left">
          <h1>Revenue Report</h1>
          <p className="date-info">
            {selectedDate.toLocaleDateString() === new Date().toLocaleDateString() 
              ? "Today's Report" 
              : `Report for ${selectedDate.toLocaleDateString()}`}
          </p>
        </div>
        <div className="header-actions">
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            dateFormat="dd/MM/yyyy"
            className="date-picker"
          />
          <button onClick={fetchReport} className="btn btn-secondary" disabled={loading}>
            Refresh
          </button>
          <button onClick={exportExcel} className="btn btn-primary" disabled={loading}>
            Export Excel
          </button>
        </div>
      </div>
      
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading report data...</p>
        </div>
      )}
      
      {reportData && (
        <div className="revenue-grid">
          <div className="main-table-container">
            <table className="revenue-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Cash</th>
                  <th>M-PESA</th>
                  <th>Mix By Yas</th>
                  <th>Selecom</th>
                  <th>CRDB Bank</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {/* Today's Revenue */}
                <tr className="revenue-row">
                  <td className="row-label">Revenue</td>
                  <td className="amount positive">{formatCurrency(reportData.revenue["CASH"])}</td>
                  <td className="amount positive">{formatCurrency(reportData.revenue["M-PESA"])}</td>
                  <td className="amount positive">{formatCurrency(reportData.revenue["MIX BY YAS"])}</td>
                  <td className="amount positive">{formatCurrency(reportData.revenue["SELECOM"])}</td>
                  <td className="amount positive">{formatCurrency(reportData.revenue["CRDB BANK"])}</td>
                  <td className="total amount positive">{formatCurrency(reportData.totalRevenue)}</td>
                </tr>
                
                {/* Today's Expenses */}
                <tr className="expense-row">
                  <td className="row-label">Expenses</td>
                  <td className="amount negative">{formatCurrency(reportData.expenses["CASH"])}</td>
                  <td className="amount negative">{formatCurrency(reportData.expenses["M-PESA"])}</td>
                  <td className="amount negative">{formatCurrency(reportData.expenses["MIX BY YAS"])}</td>
                  <td className="amount negative">{formatCurrency(reportData.expenses["SELECOM"])}</td>
                  <td className="amount negative">{formatCurrency(reportData.expenses["CRDB BANK"])}</td>
                  <td className="total amount negative">{formatCurrency(reportData.totalExpenses)}</td>
                </tr>
                
                {/* Closing Balance (Revenue - Expenses) */}
                <tr className="closing-row">
                  <td className="row-label">Closing Balance</td>
                  <td className="amount closing">{formatCurrency(reportData.closing["CASH"])}</td>
                  <td className="amount closing">{formatCurrency(reportData.closing["M-PESA"])}</td>
                  <td className="amount closing">{formatCurrency(reportData.closing["MIX BY YAS"])}</td>
                  <td className="amount closing">{formatCurrency(reportData.closing["SELECOM"])}</td>
                  <td className="amount closing">{formatCurrency(reportData.closing["CRDB BANK"])}</td>
                  <td className="total amount closing">{formatCurrency(reportData.totalClosing)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="right-panel">
            {/* Today's Expenses Breakdown */}
            <div className="detail-card">
              <h3>Expenses Breakdown</h3>
              <div className="detail-list">
                {paymentMethods.map((method) => (
                  <div className="detail-item" key={method}>
                    <span>{getMethodDisplayName(method)}</span>
                    <span className="amount negative">
                      {formatCurrency(reportData.expenses[method] || 0)}
                    </span>
                  </div>
                ))}
                <div className="detail-item total">
                  <span>Total Expenses</span>
                  <span className="amount negative">
                    {formatCurrency(reportData.totalExpenses)}
                  </span>
                </div>
              </div>
            </div>

            {/* Today's Summary Card */}
            <div className="summary-card">
              <h3>Today's Summary</h3>
              <div className="summary-list">
                <div className="summary-item">
                  <span>Revenue</span>
                  <span className="positive">{formatCurrency(reportData.totalRevenue)}</span>
                </div>
                <div className="summary-item">
                  <span>Expenses</span>
                  <span className="negative">{formatCurrency(reportData.totalExpenses)}</span>
                </div>
                <div className="summary-divider"></div>
                <div className="summary-item highlight">
                  <span>Closing Balance</span>
                  <span className={reportData.totalClosing >= 0 ? 'positive' : 'negative'}>
                    {formatCurrency(reportData.totalClosing)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}